import { debounce } from 'lodash';
import { useMemo, useState, ChangeEvent, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import { Box, Card, Table, TableBody, useMediaQuery, TableContainer } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { PageOptions } from 'src/services/api.types';
import { List, ListEntityType } from 'src/services/lists/lists.types';
import { openMultiMailerDialog } from 'src/store/slices/app/appSlice';
import { useGetListsPaginatedQuery } from 'src/services/lists/lists.service';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';

import Scrollbar from 'src/components/scrollbar';
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableSkeleton,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

import ListsTableRow from 'src/sections/lists/lists-table-row';
import ListEditorModal from 'src/sections/lists/modals/list-editor-modal';
import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import ListsTableToolbar, { ToolbarOptions } from 'src/sections/lists/lists-table-toolbar';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';


type Props = {
  entityType: ListEntityType;
  toolbarOptions?: ToolbarOptions;
};

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

export default function ListsTableView({
  entityType,
  toolbarOptions,
}: Props) {

  const table = useTable();

  const { t } = useTranslate();

  const theme = useTheme();

  const listEditorControl = useBoolean();

  const authContext = useAuthContext();

  const dispatch = useAppDispatch();

  const { canAccess } = useFeatures();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const denseHeight = table.dense ? 56 : 56 + 20;

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);
  const [filterQuery, setFilterQuery] = useState<string>('');
  const [currentList, setCurrentList] = useState<string | null>(null);

  const { currentData: lists, isFetching: isListsLoading } = useGetListsPaginatedQuery(
    {
      page: pageOptions.page,
      per_page: pageOptions.per_page,
      entity_type: entityType,
      // `undefined` rather than `''` so the param is omitted entirely when the box is empty. Until
      // JT1-1728 this was not passed at all: the toolbar wrote `filterQuery` and nothing read it,
      // which type-checks because `search_query` is optional.
      search_query: filterQuery || undefined
    },
    {
      skip: !entityType || !authContext || ![TenantType.Recruiter, TenantType.Client].includes(authContext.organization.type),
      refetchOnMountOrArgChange: true,
    }
  );

  const tableLabels = useMemo(
    () => [
      { id: 'title', label: t('lists.table.name') },
      { id: 'owner', label: t('lists.table.owner') },
      { id: 'item_count', label: t('lists.table.item_count') },
      { id: 'created_at', label: t('lists.table.created_at') },
      { id: '' },
    ],
    [t]
  );

  const notFound = !lists?.count;

  const handleFilters = useMemo(() => debounce((value: string) => {
    setFilterQuery(value);
    // Back to the first page: the current page number belongs to the UNFILTERED set, so keeping it
    // asks for (say) page 3 of a result set that now has one page, and the table renders blank.
    setPageOptions((prev) => ({ ...prev, page: 1 }));
  }, 500), [setFilterQuery]);


  const onChangePage = useCallback((_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const handleEditList = useCallback((list: List) => {
    setCurrentList(list.id);
    listEditorControl.onTrue();
  }, [listEditorControl]);

  const handleCloseListEditor = useCallback(() => {
    listEditorControl.onFalse();
    setCurrentList(null);
  }, [listEditorControl]);

  const handleOpenMailer = useCallback((list: List) => {

    let templateType: EmailMessageTemplateType;
    let targetType: MailerTargetType;

    switch (list.entity_type) {
      case ListEntityType.CANDIDATE:
        templateType = EmailMessageTemplateType.CANDIDATE_ENGAGEMENT;
        targetType = MailerTargetType.CANDIDATE;
        break;
      case ListEntityType.CONTACT:
        templateType = EmailMessageTemplateType.CONTACT_ENGAGEMENT;
        targetType = MailerTargetType.CONTACT;
        break;
      default:
        return;
    }

    dispatch(openMultiMailerDialog({
      listId: list.id,
      templateType,
      targetType,
    }));
  }, [dispatch]);

  return (
    <Card sx={{ pt: { xs: 2, sm: 'unset' } }}>

      {
        isListsLoading ? (
          <TableSkeleton />
        )
          :
          <>
            <ListsTableToolbar options={toolbarOptions} onFilters={handleFilters} onAddList={listEditorControl.onTrue} />

            <TableContainer sx={{ overflow: 'unset' }}>
              <Scrollbar>

                {
                  canAccess(FeatureType.LISTS_ENABLED) ?
                    <Table size={table.dense ? 'small' : 'medium'}>

                      {
                        !isMobile && (
                          <TableHeadCustom headLabel={tableLabels} />
                        )
                      }

                      <TableBody >
                        {lists?.results?.map((row) => <ListsTableRow key={row.id} row={row} onEditList={handleEditList} onSendMultiEmail={handleOpenMailer} />)}

                        <TableEmptyRows
                          height={denseHeight}
                          emptyRows={emptyRows(table.page, table.rowsPerPage, lists?.count ?? 0)}
                        />

                        <TableNoData notFound={notFound} />
                      </TableBody>
                    </Table>
                    :
                    <Box >
                      <FeatureLockedListView text={t('billing.subscription_required')} />
                    </Box>
                }

              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={lists?.count ?? 0}
              page={pageOptions.page}
              rowsPerPage={pageOptions.per_page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              dense={table.dense}
              onChangeDense={table.onChangeDense}
            />
          </>
      }


      <ListEditorModal open={listEditorControl.value} onClose={handleCloseListEditor} entityType={entityType} listId={currentList} />
    </Card>
  );
}
